// The clickable count tiles of the Endpoint Devices summary: one set for the Windows updates status of the machines,
// one for their known vulnerabilities (CVEs). Each tile knows which machines it counts, so clicking it can list them.
// Plain functions only (no React), so they can be unit tested.
import { CVE_STATUS_COLORS, cveNumbers, cveStatusCounts, cveStatusOf } from './cveSummary';
import { STATUS_COLORS, statusCounts, statusOf } from './updateSummary';
import {
  AGE_LIMIT_YEARS, isHealthy, isNotAssessed, isOlderThanLimit, isPlanReplacement, isReplaceNow,
} from './systemInfoSummary';

export const UPDATES = 'updates';
export const CVE = 'cve';
export const REPLACEMENT = 'replacement';
export const SOFTWARE = 'software';       // a program family of the Top software card

// { id, label, count, color, matches(item) } per tile. "All machines" comes first.
export const updateTiles = (items) => {
  const list = items || [];
  return [
    { id: 'all', label: 'All machines', count: list.length, color: 'default', matches: () => true },
    ...statusCounts(list).map(({ status, count }) => ({
      id: status,
      label: status,
      count,
      color: STATUS_COLORS[status.toLowerCase()] || 'default',
      matches: (item) => statusOf(item) === status,
    })),
  ];
};

export const UNUSED_TILE = 'unused-with-cves';
export const WINDOWS_TILE = 'windows-cves';

// The CVE statuses worst first (by the software; the Windows operating system is counted apart), then "not in use,
// with CVEs": the machines that have a program with CVEs that nobody runs, which is a remove-or-update decision, and
// the machines whose Windows itself has CVEs, which Windows updates fix.
export const cveTiles = (items) => {
  const list = items || [];
  const tiles = cveStatusCounts(list).map(({ status, count }) => ({
    id: status,
    label: status,
    count,
    color: CVE_STATUS_COLORS[status] || 'default',
    matches: (item) => cveStatusOf(item) === status,
  }));
  const unused = list.filter((item) => cveNumbers(item).unusedWithCves > 0).length;
  if (unused > 0) {
    tiles.push({
      id: UNUSED_TILE,
      label: 'Has unused programs with CVEs',
      count: unused,
      color: 'warning',
      matches: (item) => cveNumbers(item).unusedWithCves > 0,
    });
  }
  const windows = list.filter((item) => cveNumbers(item).windows > 0).length;
  if (windows > 0) {
    tiles.push({
      id: WINDOWS_TILE,
      label: 'Windows itself has CVEs (needs updates)',
      count: windows,
      color: 'info',
      matches: (item) => cveNumbers(item).windows > 0,
    });
  }
  return tiles;
};

// Replacement and age: the machines to replace now, to plan for, older than the limit, fine, and not reported yet.
// A part with no machines is left out.
export const replacementTiles = (items) => {
  const list = items || [];
  if (list.length === 0) return [];
  const tile = (id, label, color, matches) => ({ id, label, count: list.filter(matches).length, color, matches });
  const tiles = [
    tile('replace-now', 'Replace now', 'error', isReplaceNow),
    tile('plan-replacement', 'Plan replacement', 'warning', isPlanReplacement),
    tile('older', `Older than ${AGE_LIMIT_YEARS} years`, 'info', isOlderThanLimit),
    tile('healthy', 'Healthy', 'success', isHealthy),
    tile('not-assessed', 'Not assessed yet', 'default', isNotAssessed),
  ];
  return tiles.filter((t) => t.count > 0);
};

// The tab a machine opens on, by the kind of tile it was listed under.
export const tabFor = (kind) => {
  if (kind === CVE || kind === SOFTWARE) return 'software';
  if (kind === REPLACEMENT) return 'systeminfo';
  return 'updates';
};

// The address of one machine's page. The account and device type go along, because the scripts are scheduled
// per account and device type.
export const deviceUrl = (deviceId, accountCode, deviceType, kind) => {
  const q = new URLSearchParams({ account: accountCode, type: deviceType, tab: tabFor(kind) });
  return `/endpointdevices/${encodeURIComponent(deviceId)}?${q.toString()}`;
};
